/**
 * The wish list's elements. The data lives in assets/wishlist-store.js; read its header first.
 *
 *   <wishlist-button>  the heart on a product card and the product page's "Save to wish list".
 *                      Wraps a real <button type="button" aria-pressed>. Pressed means saved.
 *   <wishlist-count>   the header's heart and the menu drawer's row, with the number saved.
 *   <wishlist-page>    /pages/wishlist (templates/page.wishlist.json): renders each saved handle
 *                      as the shop's own product card, fetched from templates/product.wishlist-card.json.
 *
 * NO JAVASCRIPT, NO HEART. Every one of these is invisible until this module defines it
 * (`:not(:defined)` in snippets/wishlist-styles.liquid keeps its box, so nothing moves when it
 * appears), and layout/theme.liquid removes them outright under <noscript>. A heart that cannot
 * save is never on screen. gate:wishlist holds both halves.
 *
 * WHY THE LIST PAGE FETCHES ?view=wishlist-card AND NOT ?section_id=. The plan named the Section
 * Rendering API. Measured on preview 208733503818, 2026-10-01: `?section_id=<key>` for a section in
 * a JSON template returns 404, and only the full `template--<id>__<key>` form renders. That id
 * belongs to one template on one theme, is not readable from any other page's Liquid, and changes
 * when the theme is duplicated for go-live. templates/product.wishlist-card.json is therefore an
 * alternate product template with `"layout": false`: requested with `?view=`, it returns exactly
 * what the Section Rendering API would (one section, no header, no footer), with a stable address.
 */

import { announce } from '@theme/live-status';
import {
  CHANGE_EVENT,
  MAX_ITEMS,
  STORAGE_KEY,
  countLabel,
  createConsentGate,
  createStore,
  sendAddedEvent,
  usableStorage,
} from '@theme/wishlist-store';

const config = /** @type {HTMLScriptElement | null} */ (document.querySelector('script[data-wishlist-config]'));
const strings = {
  one: config?.dataset.countOne ?? '',
  other: config?.dataset.countOther ?? '',
  saved: config?.dataset.savedNotice ?? '',
  removed: config?.dataset.removedNotice ?? '',
  full: config?.dataset.fullNotice ?? '',
};

const store = createStore(usableStorage(() => window.localStorage));
const gate = createConsentGate();

/** @param {string} handle @param {boolean} saved */
function changed(handle, saved) {
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { handle, saved } }));
}

/**
 * Write an attribute or text only when it differs. Both elements re-sync from a MutationObserver,
 * so an unconditional write would observe itself forever.
 * @param {Element} el @param {string} name @param {string | null} value null removes it
 */
function setAttr(el, name, value) {
  if (value === null) {
    if (el.hasAttribute(name)) el.removeAttribute(name);
  } else if (el.getAttribute(name) !== value) {
    el.setAttribute(name, value);
  }
}

/**
 * THE HEADER IS MORPHED AFTER LOAD. sections/header.liquid hydrates itself at idle
 * (assets/section-hydration.js) and morphs the server's HTML back over the menu drawer, which put
 * the drawer row's count back to empty and hidden while the element itself stayed connected, so
 * connectedCallback never ran again (measured on preview 208733503818, 2026-10-01: header count 2,
 * drawer count blank). Each element therefore watches its own subtree and re-applies the stored
 * state whenever something else rewrites it.
 * @param {HTMLElement} host @param {() => void} sync
 */
function watch(host, sync) {
  const observer = new MutationObserver(sync);
  observer.observe(host, { subtree: true, childList: true, attributes: true, characterData: true });
  return observer;
}

// Another tab saved or removed a wine: every heart and count on this page follows.
window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY || event.key === null) changed('', false);
});

class WishlistButton extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #button = null;
  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    this.#button = this.querySelector('button');
    if (!store.available || !this.#button || !this.dataset.handle) {
      this.hidden = true;
      return;
    }
    this.#sync();
    this.#button.addEventListener('click', this.#onClick);
    document.addEventListener(CHANGE_EVENT, this.#sync);
    this.#observer = watch(this, this.#sync);
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#onClick);
    document.removeEventListener(CHANGE_EVENT, this.#sync);
    this.#observer?.disconnect();
  }

  #sync = () => {
    const button = this.querySelector('button');
    if (button && button !== this.#button) {
      // Morphed in a new button: move the listener onto it.
      this.#button?.removeEventListener('click', this.#onClick);
      this.#button = /** @type {HTMLButtonElement} */ (button);
      this.#button.addEventListener('click', this.#onClick);
    }
    if (this.#button) setAttr(this.#button, 'aria-pressed', String(store.has(this.dataset.handle ?? '')));
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    // The heart sits on the card's photo link; a press saves, it never navigates.
    event.preventDefault();
    const handle = this.dataset.handle ?? '';
    if (store.isFull(handle)) {
      // Refused, not trimmed: the heart stays unpressed and the visitor is told why.
      announce(strings.full.replace(/\[count\]/g, String(MAX_ITEMS)));
      return;
    }
    const saved = store.toggle(handle);
    this.#sync();
    changed(handle, saved);
    announce(`${saved ? strings.saved : strings.removed}. ${countLabel(store.count(), strings)}`);
    if (saved) {
      sendAddedEvent(gate, {
        id: this.dataset.productId,
        handle,
        title: this.dataset.title,
        url: this.dataset.url,
        image: this.dataset.image,
        price: this.dataset.price,
        vendor: this.dataset.vendor,
      });
    }
  };
}

class WishlistCount extends HTMLElement {
  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    if (!store.available) {
      this.hidden = true;
      return;
    }
    this.#sync();
    document.addEventListener(CHANGE_EVENT, this.#sync);
    this.#observer = watch(this, this.#sync);
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.#sync);
    this.#observer?.disconnect();
  }

  #sync = () => {
    const count = store.count();
    const link = this.querySelector('a');
    const number = this.querySelector('[data-wishlist-number]');
    if (number instanceof HTMLElement) {
      const text = count > 0 ? String(Math.min(count, 99)) : '';
      if (number.textContent !== text) number.textContent = text;
      setAttr(number, 'hidden', count === 0 ? '' : null);
    }
    if (link) {
      const base = link.dataset.label ?? '';
      setAttr(link, 'aria-label', count > 0 ? `${base}, ${countLabel(count, strings)}` : base);
    }
  };
}

const CONCURRENCY = 4;

class WishlistPage extends HTMLElement {
  connectedCallback() {
    this.#render();
    document.addEventListener(CHANGE_EVENT, this.#summarise);
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.#summarise);
  }

  /** @param {string} selector */
  #part(selector) {
    return /** @type {HTMLElement | null} */ (this.querySelector(selector));
  }

  #summarise = () => {
    const summary = this.#part('[data-wishlist-summary]');
    if (!summary) return;
    const count = store.count();
    summary.textContent = count > 0 ? countLabel(count, strings) : '';
  };

  async #render() {
    const grid = this.#part('[data-wishlist-grid]');
    const empty = this.#part('[data-wishlist-empty]');
    const error = this.#part('[data-wishlist-error]');
    if (!grid || !empty) return;

    const handles = store.available ? store.list() : [];
    if (handles.length === 0) {
      grid.replaceChildren();
      empty.hidden = false;
      this.#summarise();
      return;
    }
    empty.hidden = true;

    // One placeholder per saved wine, card-shaped, so the footer does not jump as cards arrive.
    // sections/wishlist.liquid has usually put the same number in before first paint already;
    // replacing them in one go draws nothing new.
    grid.replaceChildren();
    const cells = handles.map((handle) => {
      const cell = document.createElement('li');
      cell.className = 'wishlist__item wishlist__item--loading';
      cell.dataset.handle = handle;
      cell.setAttribute('aria-hidden', 'true');
      grid.append(cell);
      return cell;
    });
    grid.setAttribute('aria-busy', 'true');
    this.#summarise();

    let failed = 0;
    let next = 0;
    const work = async () => {
      while (next < handles.length) {
        const index = next++;
        const handle = /** @type {string} */ (handles[index]);
        const cell = /** @type {HTMLLIElement} */ (cells[index]);
        const result = await this.#fetchCard(handle);
        if (result === 'gone') {
          // Deleted, unpublished or renamed: drop it so the list stops asking.
          store.remove(handle);
          cell.remove();
        } else if (result === 'failed') {
          failed++;
          cell.remove();
        } else {
          cell.replaceChildren(...result);
          cell.classList.remove('wishlist__item--loading');
          cell.removeAttribute('aria-hidden');
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, handles.length) }, work));

    grid.removeAttribute('aria-busy');
    this.#summarise();
    if (error) error.hidden = failed === 0;
    if (!grid.children.length && failed === 0) empty.hidden = false;
  }

  /**
   * @param {string} handle
   * @returns {Promise<Node[] | 'gone' | 'failed'>}
   */
  async #fetchCard(handle) {
    const root = window.Shopify?.routes?.root ?? '/';
    const view = this.dataset.view ?? 'wishlist-card';
    try {
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}?view=${encodeURIComponent(view)}`, {
        headers: { Accept: 'text/html' },
      });
      if (response.status === 404) return 'gone';
      if (!response.ok) return 'failed';
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const card = doc.querySelector('[data-wishlist-card]');
      if (!card) return 'failed';
      // An empty card is a wine the shop no longer offers (a retired club case): drop it.
      if (!card.children.length) return 'gone';
      return Array.from(card.children).map((node) => document.adoptNode(node));
    } catch {
      return 'failed';
    }
  }
}

if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);
if (!customElements.get('wishlist-count')) customElements.define('wishlist-count', WishlistCount);
if (!customElements.get('wishlist-page')) customElements.define('wishlist-page', WishlistPage);
